import { DataTableV2, Highlight, Text, Title } from "components/commons";
import React, { useCallback, useMemo, useState } from "react";
import locale from "localization";
import styles from "../shift-records-details-cash-and-non-cash.module.scss";
import { initialFilterState } from "modules/shift-records/shift-records-filter.state";
import useDataTable from "hooks/useDataTable";
import { isArray, sortBy } from "lodash";
import { nonCashSummaryColumn } from "./shift-details-non-cash-summary-column";
import { nonCashSummaryMapper } from "./shift-details-non-cash-summary.mapper";
import { FormMode, PaymentMethod } from "enums";
import { isAccountabilityEnabled } from "data/constant/accountability";
import PaymentMethodTransactionModal from "./payment-method-transaction-modal/payment-method-transaction-modal";
import { useModal } from "hooks";
import { prettifyPaymentMethod } from "utils/pretty.utils";
import Big from "big.js";
import ReminderHexIcon from "./reminder-hex-icon";

export default function ShiftDetailsNonCashSummary({
  shift,
  refetchShift,
  pageMode,
  updatePaymentMethodRefNumber,
  updateTransactionPaymentMethod,
  updateTransactionAccountability,
  employees,
}) {
  const paymentMethodTransactionModal = useModal();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState([]);

  const displayedMethods = useMemo(() => Object.values(PaymentMethod).map((value) => value), []);

  const transactionsItems = useMemo(() => {
    const result = [];
    Object.values(shift.transactionItem).forEach((productTransaction) => {
      if (isArray(productTransaction)) {
        result.push(...productTransaction.map((t) => t.transaction));
      }
    });

    return result;
  }, [shift.transactionItem]);

  const transactionsItemDetails = useMemo(() => {
    const result = [];
    Object.values(shift.transactionItem).forEach((productTransaction) => {
      if (isArray(productTransaction)) {
        result.push(...productTransaction.map((t) => t));
      }
    });

    return result;
  }, [shift.transactionItem]);

  const [hasReferenceNumberChange, setHasReferenceNumberChange] = useState(false);

  const handleUpdatePaymentMethodRefNumber = useCallback(
    (transactionPaymentMethodId, newRefNumber) => {
      setHasReferenceNumberChange(true);
      updatePaymentMethodRefNumber?.(transactionPaymentMethodId, newRefNumber);
    },
    [updatePaymentMethodRefNumber],
  );

  const handleUpdateTransactionPaymentMethod = useCallback(
    (transactionPaymentMethodId, changes) => {
      setHasReferenceNumberChange(true);
      updateTransactionPaymentMethod?.(transactionPaymentMethodId, changes);
    },
    [updateTransactionPaymentMethod],
  );

  // const mapPaymentTransaction = useCallback(
  //   (t) => {
  //     const {
  //       totalAmount,
  //       finalAmountPaid: finalAmount,
  //       totalDiscount: rawDiscount,
  //       displayExcessAmount: excessShare,
  //       hasDiscount: isActualDiscount,
  //       isPremium,
  //     } = getCashAndNonCashTotals({
  //       payment: t,
  //       transactionsItemDetails: saleTransactionsDetailed,
  //     });

  //     // STOCQ-5888: keep `discount` negative for premium rows so a later re-evaluation
  //     // (e.g. the detailed modal calling getCashAndNonCashTotals on this enriched row)
  //     // re-detects the premium via the `discount < 0` + `mActualDiscount <= 0` signals.
  //     const discount = isPremium
  //       ? -Math.abs(rawDiscount || 0)
  //       : !isActualDiscount && rawDiscount > 0
  //         ? -rawDiscount
  //         : rawDiscount;
  //     // STOCQ-5888: VAT exempt math doesn't apply for premium rows — final exceeds total
  //     // by design, so the implied "VAT-removed" residual is meaningless.
  //     const impliedVatAdj = totalAmount - Math.abs(discount) - finalAmount;
  //     const vatExemptAmount = !isPremium && impliedVatAdj > 0.005 ? -impliedVatAdj : 0;

  //     return {
  //       ...t,
  //       totalAmount,
  //       finalAmount,
  //       excessAmount: excessShare,
  //       discount,
  //       vatExemptAmount,
  //       isPremium: isPremium === true,
  //     };
  //   },
  //   [saleTransactionsDetailed],
  // );

  // const mapPaymentTransaction = useCallback(
  //   (t) => {
  //     const sourceItem = saleTransactionsDetailed.find(
  //       (sale) => sale.transactionItemId === t.transactionItemId
  //     );

  //     const transactionItemLength = saleTransactionsDetailed.filter(
  //       (sale) => sale.transactionId === t.transactionId
  //     ).length;

  //     // ✅ Excess: proportional by product gross / transaction gross
  //     const productGross = sourceItem?.grossTotalAmount || 0;
  //     const transactionGross = sourceItem?.transaction?.grossTotalAmount || 1;
  //     const excessShare = ((productGross / transactionGross) * t.excessAmount * 100) / 100;

  //     // ✅ Payment face value for POS-level discount and amount input totalAmount
  //     const paymentMethod = sourceItem?.transaction?.paymentMethods?.find(
  //       (pm) => pm.transactionPaymentMethodId === t.transactionPaymentMethodId
  //     );
  //     const paymentFaceValue = paymentMethod?.amount || 0;
  //     const tenderedAmount = sourceItem?.transaction?.tenderedAmount || 1;

  //     // ✅ Detect amount input — sourceItem quantity is clean up to 3 decimal places
  //     const isAmountInput =
  //       t.type === "fuel" &&
  //       sourceItem?.quantity === parseFloat(Number(sourceItem?.quantity).toFixed(2));

  //     const isNotSplitPayment =
  //       shift.transactionPaymentMethod.nonCash
  //         .filter((nonCash) => nonCash.transactionId === t.transactionId)
  //         .every((nonCash, _, arr) => nonCash.method === arr[0].method) &&
  //       shift.transactionPaymentMethod.cash.filter((cash) => cash.transactionId === t.transactionId)
  //         .length === 0; // ✅ no cash payments in this transaction

  //     const withCashPayment =
  //       shift.transactionPaymentMethod.cash.filter((cash) => cash.transactionId === t.transactionId)
  //         .length > 0;

  //     // ✅ totalAmount:
  //     //    amount input → truncate(paymentFaceValue / tenderedAmount × productGross, 2)
  //     //    liters input → Math.round(quantity × pricePerUnit, 2)
  //     const pricePerUnit =
  //       t.type === "fuel"
  //         ? shift.shift.pricing.find((p) => p.product === nameToSku(t.name))?.pricePerLiter || 0
  //         : sourceItem?.originalPricePerItem || 0;

  //     let totalAmountRaw = isAmountInput
  //       ? (paymentFaceValue / tenderedAmount) * productGross
  //       : (t.type === "fuel" ? truncate(t.quantity, 3) : t.quantity) * pricePerUnit;

  //     if (Math.abs(totalAmountRaw - t.totalAmount) <= 0.1) {
  //       totalAmountRaw = t.type === "fuel" ? truncate(t.quantity * pricePerUnit, 2) : t.totalAmount;
  //     }

  //     let pumpDiscountVat12Percent = t.quantity * sourceItem?.pumpDiscountPerItem;
  //     if (sourceItem?.vat === 0) {
  //       pumpDiscountVat12Percent = pumpDiscountVat12Percent - sourceItem.totalPumpDiscount;
  //     } else {
  //       pumpDiscountVat12Percent = 0;
  //     }

  //     let normalizedDiscount =
  //       sourceItem?.discountValue > 0 ? Math.abs(t.discount) : -Math.abs(t.discount);
  //     if (sourceItem?.vat === 0 && sourceItem.discount !== "" && sourceItem.discountValue < 0) {
  //       normalizedDiscount = sourceItem.discountValue;
  //     }

  //     let totalAmount = withCashPayment
  //       ? totalAmountRaw
  //       : isAmountInput
  //       ? isNotSplitPayment
  //         ? sourceItem.grossTotalAmount + pumpDiscountVat12Percent
  //         : t.quantity * pricePerUnit
  //       : t.totalAmount - t.excessAmount - (normalizedDiscount > 0 ? normalizedDiscount : 0);

  //     // if (t.discount > 0) {
  //     //   totalAmount += -t.discount;
  //     // }

  //     // ✅ Discount computation
  //     const allTransactionItems = saleTransactionsDetailed.filter(
  //       (sale) => sale.transactionId === t.transactionId
  //     );
  //     const combinedTotalDiscount = allTransactionItems.reduce(
  //       (sum, item) => sum + Math.abs(item.totalDiscount || 0),
  //       0
  //     );

  //     // ✅ Step 1: POS-level discount for this payment method
  //     const posLevelDiscount =
  //       Math.round((paymentFaceValue / tenderedAmount) * truncate(combinedTotalDiscount, 2) * 100) /
  //       100;

  //     const computeDiscount = () => {
  //       return -(
  //         (((isNotSplitPayment || withCashPayment ? t.totalAmount : totalAmount) /
  //           transactionGross) *
  //           combinedTotalDiscount *
  //           100) /
  //         100
  //       );
  //     };

  //     let discount =
  //       normalizedDiscount > 0 || isNotSplitPayment ? normalizedDiscount : computeDiscount();

  //     const isGiftCardTransaction = shift.transactionPaymentMethod.nonCash
  //       .filter((nonCash) => nonCash.transactionId === t.transactionId)
  //       .every((nonCash) => nonCash.method === PaymentMethod.GiftCard);

  //     if (isGiftCardTransaction) {
  //       discount = normalizedDiscount > 0 ? normalizedDiscount : computeDiscount();
  //     }

  //     const isVatOnly =
  //       sourceItem?.vat === 0 && sourceItem.discount === "" && sourceItem.pumpDiscountPerItem === 0;

  //     if (isVatOnly) {
  //       discount = 0;
  //     }

  //     const vatExempt = sourceItem?.vat === 0 ? -(totalAmount - totalAmount / 1.12) || 0 : 0;
  //     // const finalAmount =
  //     //   truncate(totalAmount, 1) +
  //     //   truncate(discount, 1) +
  //     //   truncate(vatExempt, 1) +
  //     //   truncate(excessShare, 1);
  //     const finalAmount =
  //       (withCashPayment ? Big(totalAmount).round(2).toNumber() : totalAmount) +
  //       discount +
  //       vatExempt +
  //       excessShare;
  //     const finalAmountWithoutExcess = totalAmount + discount + vatExempt;

  //     // if (t.method === "card") {
  //     //   console.log("=== DISCOUNT DEEP DEBUG ===", {
  //     //     t_totalAmount: t.totalAmount,
  //     //     t_discount: t.discount,
  //     //     truncate_totalAmountRaw: truncate(totalAmountRaw, 2),
  //     //     truncate_combinedTotalDiscount: truncate(combinedTotalDiscount, 2),
  //     //     step1: truncate(totalAmountRaw, 2) / transactionGross,
  //     //     step2:
  //     //       (truncate(totalAmountRaw, 2) / transactionGross) * truncate(combinedTotalDiscount, 2),
  //     //     final:
  //     //       Math.round(
  //     //         (truncate(totalAmountRaw, 2) / transactionGross) *
  //     //           truncate(combinedTotalDiscount, 2) *
  //     //           100
  //     //       ) / 100,
  //     //     // also try posLevelDiscount path
  //     //     posPath: Math.round(((posLevelDiscount * productGross) / transactionGross) * 100) / 100,
  //     //     posPathRaw: (1000 / 1220) * 19.34 * (1014.88 / 1384.88),
  //     //   });
  //     // }
  //     return {
  //       ...t,
  //       totalAmount: Big(totalAmount).round(2).toNumber(),
  //       finalAmount,
  //       excessAmount: excessShare,
  //       transactionItemLength,
  //       discount,
  //       vatExempt,
  //       finalAmountWithoutExcess,
  //     };
  //   },
  //   [
  //     saleTransactionsDetailed,
  //     shift.transactionPaymentMethod.cash,
  //     shift.transactionPaymentMethod.nonCash,
  //     shift.shift.pricing,
  //   ]
  // );

  const mapPaymentTransaction = useCallback((t) => {
    return t;
  }, []);

  const sorted = useMemo(() => {
    const nonCashRefunds = (shift?.transactionPaymentMethod.refund || []).filter(
      (r) => r.method !== "cash",
    );
    const transactions = sortBy(
      (shift?.transactionPaymentMethod.nonCash || []).map((nonCash) => {
        const refund = nonCashRefunds.find(
          (r) => r.sku === nonCash.sku && r.method === nonCash.method,
        );
        if (!refund) return nonCash;
        const refundDiscount =
          refund.discount !== 0 && refund.vatAmount !== 0
            ? Big(refund.discount).add(refund.vatAmount).toNumber()
            : refund.discount;

        const quantity = Big(nonCash.quantity).add(refund.quantity).toNumber();
        const totalAmount = Big(nonCash.totalAmount).add(refund.totalAmount).toNumber();
        const discount = Big(nonCash.discount).minus(refundDiscount).toNumber();
        const vatAmount = Big(nonCash.vatAmount).add(refund.vatAmount).toNumber();
        const finalAmount = Big(totalAmount).add(discount).add(vatAmount).toNumber();

        return {
          ...nonCash,
          quantity,
          totalAmount,
          finalAmount,
          discount,
          vatAmount,
        };
      }),
      "transactionItemId",
    ).reverse();

    const groupedPayments = {};
    transactions
      .filter((payment) => displayedMethods.includes(payment.method))
      .forEach((payment) => {
        const label = prettifyPaymentMethod(payment.method);
        groupedPayments[label] = [...(groupedPayments[label] || []), payment];
      });

    const result = [];
    Object.values(groupedPayments).forEach((value) => {
      let resultTotalAmount = 0;
      let resultFinalAmount = 0;
      let resultExcessAmount = 0;
      let resultVatExempt = 0;
      let resultDiscount = 0;
      let resultPremium = 0;
      value.forEach((val) => {
        const { totalAmount, finalAmount, excessAmount, discount, vatAmount } =
          mapPaymentTransaction(val);

        resultTotalAmount += totalAmount;
        resultFinalAmount += finalAmount;
        resultVatExempt += vatAmount;
        resultExcessAmount += excessAmount;
        resultDiscount += discount;

        // STOCQ-5886/5887: per-row `discount` from mapPaymentTransaction may carry mixed signs
        // (fuel-pump-only computed branch returns negative, real txn discount returns positive).
        // Both are shiftDiscount — sum by magnitude so they accumulate instead of cancelling.
        // STOCQ-5888: a premium row's magnitude is tracked separately so the summary mapper
        // can render it as positive plain (not "-X.XX" red).
        // if (isPremium) {
        //   resultPremium += Math.abs(discount || 0);
        // } else {
        //   resultDiscount += Math.abs(discount || 0);
        // }
        // if (!seenPmIds.has(val.transactionPaymentMethodId)) {
        //   seenPmIds.add(val.transactionPaymentMethodId);
        //   resultExcessAmount += excessAmount;
        // }
      });
      // resultExcessAmount = resultExcessAmount - (floating ? 0.01 : 0);
      // resultFinalAmount += resultExcessAmount;

      result.push({
        transactionCount: [...new Set(value.map((t) => t.transactionId))].length,
        adjustmentCount: [
          ...new Set(
            value
              .filter(
                (t) =>
                  t.adjustedAt &&
                  ![PaymentMethod.pumpCalibration, PaymentMethod.pumpError].includes(t.method),
              )
              .map((t) => t.transactionId),
          ),
        ].length,
        actionNeeded: isAccountabilityEnabled && value.some((t) => t.actionNeeded),
        method: value[0].method,
        methods: [...new Set(value.map((t) => t.method))],
        totalAmount: Big(resultTotalAmount).round(2).toNumber(),
        finalAmount: resultFinalAmount,
        excessAmount: resultExcessAmount,
        discount: resultDiscount,
        premium: resultPremium,
        vatAmount: resultVatExempt,
      });
    });

    return result.sort((a, b) =>
      prettifyPaymentMethod(a.method).localeCompare(prettifyPaymentMethod(b.method)),
    );
  }, [
    displayedMethods,
    mapPaymentTransaction,
    shift?.transactionPaymentMethod.nonCash,
    shift?.transactionPaymentMethod.refund,
  ]);

  const nonCashTransactions = useMemo(() => {
    const nonCashRefunds = (shift?.transactionPaymentMethod.refund || []).filter(
      (r) => r.method !== "cash",
    );
    return sortBy(
      (shift?.transactionPaymentMethod.nonCash || []).map((nonCash) => {
        const refund = nonCashRefunds.find(
          (r) => r.sku === nonCash.sku && r.method === nonCash.method,
        );
        if (!refund) return nonCash;
        const refundDiscount =
          refund.discount !== 0 && refund.vatAmount !== 0
            ? Big(refund.discount).add(refund.vatAmount).toNumber()
            : refund.discount;

        const quantity = Big(nonCash.quantity).add(refund.quantity).toNumber();
        const totalAmount = Big(nonCash.totalAmount).add(refund.totalAmount).toNumber();
        const discount = Big(nonCash.discount).minus(refundDiscount).toNumber();
        const vatAmount = Big(nonCash.vatAmount).add(refund.vatAmount).toNumber();
        const finalAmount = Big(totalAmount).add(discount).add(vatAmount).toNumber();

        return {
          ...nonCash,
          quantity,
          totalAmount,
          finalAmount,
          discount,
          vatAmount,
        };
      }),
      "transactionItemId",
    ).reverse();
  }, [shift?.transactionPaymentMethod.nonCash, shift?.transactionPaymentMethod.refund]);

  // const paymentMethodTransactions = useMemo(() => {
  //   const transactions = sortBy(
  //     shift?.transactionPaymentMethod.nonCash,
  //     "transactionItemId"
  //   ).reverse();

  //   let filtered;
  //   if (selectedPaymentMethod === PaymentMethod.Other) {
  //     filtered = transactions.filter((payment) => !displayedMethods.includes(payment.method));
  //   } else {
  //     filtered = transactions.filter((transaction) => transaction.method === selectedPaymentMethod);
  //   }

  //   const pmGroups = {};
  //   filtered.forEach((t) => {
  //     const key = t.transactionPaymentMethodId;
  //     if (!pmGroups[key]) pmGroups[key] = { total: 0, excess: t.excessAmount };
  //     pmGroups[key].total += t.totalAmount - t.excessAmount;
  //   });

  //   // STOCQ-5869: raw per-row `discount` is segregated by discount source — fuel rows carry
  //   // only their pump-discount share, lubes rows carry only their transaction-discount share.
  //   // The detailed view must show each row's share of the COMBINED transaction discount
  //   // (pump + transaction) distributed proportionally by amount across all line items. To stay
  //   // consistent with QA (Amount + Excess − FinalAmountPaid = Discount), derive each row's
  //   // discount from the proportional Final Amount Paid rather than rounding discount directly,
  //   // since independent rounding of discount and final can disagree by 0.01.
  //   const transactionGroups = {};
  //   filtered.forEach((t) => {
  //     const key = t.transactionId;
  //     if (!transactionGroups[key]) {
  //       transactionGroups[key] = { totalAmount: 0, totalDiscount: 0, totalExcess: 0 };
  //     }
  //     transactionGroups[key].totalAmount += t.totalAmount - (t.excessAmount || 0);
  //     transactionGroups[key].totalDiscount += Math.abs(t.discount || 0);
  //     transactionGroups[key].totalExcess += t.excessAmount || 0;
  //   });

  //   filtered = filtered.map((t) => {
  //     const originalTotalAmount = t.totalAmount;
  //     const group = pmGroups[t.transactionPaymentMethodId];
  //     const mAmount = originalTotalAmount - t.excessAmount;
  //     const ratio = group.total > 0 ? mAmount / group.total : 0;
  //     const proportionalExcess = Math.round(ratio * group.excess * 100) / 100;

  //     const enriched = mapPaymentTransaction(t);
  //     const result = { ...enriched, totalAmount: originalTotalAmount, proportionalExcess };

  //     // Only redistribute when the transaction has Excess. Without excess the per-row raw
  //     // discount values are what QA already accepted (e.g. STOCQ-5832); redistribution would
  //     // change those without justification. With excess, QA expects combined-discount
  //     // proportional distribution so Amount + Excess − FinalAmountPaid is consistent per row.
  //     const txnGroup = transactionGroups[t.transactionId];
  //     if (
  //       txnGroup &&
  //       txnGroup.totalAmount > 0 &&
  //       txnGroup.totalDiscount > 0 &&
  //       txnGroup.totalExcess > 0
  //     ) {
  //       const totalFinal = txnGroup.totalAmount + txnGroup.totalExcess - txnGroup.totalDiscount;
  //       const proportionalFinal =
  //         Math.round((mAmount * totalFinal * 100) / txnGroup.totalAmount) / 100;
  //       let proportionalDiscount =
  //         Math.round((mAmount + proportionalExcess - proportionalFinal) * 100) / 100;
  //       if (proportionalDiscount < 0) proportionalDiscount = 0;
  //       result.proportionalDiscount = proportionalDiscount;
  //     }

  //     return result;
  //   });

  //   return filtered;
  // }, [
  //   displayedMethods,
  //   mapPaymentTransaction,
  //   selectedPaymentMethod,
  //   shift?.transactionPaymentMethod.nonCash,
  // ]);

  const paymentMethodTransactions = useMemo(() => {
    const nonCashRefunds = (shift?.transactionPaymentMethod.refund || []).filter(
      (r) => r.method !== "cash",
    );
    const transactions = sortBy(shift?.transactionPaymentMethod.nonCash, "transactionItemId")
      .map((nonCash) => {
        const refund = nonCashRefunds.find(
          (r) => r.sku === nonCash.sku && r.method === nonCash.method,
        );
        if (!refund) return nonCash;
        const refundDiscount =
          refund.discount !== 0 && refund.vatAmount !== 0
            ? Big(refund.discount).add(refund.vatAmount).toNumber()
            : refund.discount;

        const quantity = Big(nonCash.quantity).add(refund.quantity).toNumber();
        const totalAmount = Big(nonCash.totalAmount).add(refund.totalAmount).toNumber();
        const discount = Big(nonCash.discount).minus(refundDiscount).toNumber();
        const vatAmount = Big(nonCash.vatAmount).add(refund.vatAmount).toNumber();
        const finalAmount = Big(totalAmount).add(discount).add(vatAmount).toNumber();
        return {
          ...nonCash,
          quantity,
          totalAmount,
          discount,
          vatAmount,
          finalAmount,
        };
      })
      .reverse();

    let filtered;
    if (selectedPaymentMethod === PaymentMethod.Other) {
      filtered = transactions.filter((payment) => !displayedMethods.includes(payment.method));
    } else {
      filtered = transactions.filter((transaction) =>
        selectedPaymentMethods.includes(transaction.method),
      );
    }

    filtered = filtered.map((t) => {
      return mapPaymentTransaction(t);
    });

    return filtered.sort((a, b) =>
      String(b.transactionNumber ?? "").localeCompare(String(a.transactionNumber ?? ""), undefined, {
        numeric: true,
      }),
    );
  }, [
    displayedMethods,
    mapPaymentTransaction,
    selectedPaymentMethod,
    selectedPaymentMethods,
    shift?.transactionPaymentMethod.nonCash,
  ]);

  const onViewPaymentMethodTransaction = useCallback(
    (method, methods = []) => {
      paymentMethodTransactionModal.show({});
      setSelectedPaymentMethod(method);
      setSelectedPaymentMethods(methods.length ? methods : [method]);
    },
    [paymentMethodTransactionModal],
  );

  const { table } = useDataTable({
    value: sorted || [],
    filter: { initialState: initialFilterState(), mapper: (data) => data },
    table: {
      key: "nonCash",
      name: "nonCashTransactions",
      mapper: (val) =>
        nonCashSummaryMapper({
          val,
          onViewPaymentMethodTransaction,
          nonCashTransactions: nonCashTransactions,
          transactionsItemDetails,
          isView: pageMode === FormMode.View,
        }),
      columns: nonCashSummaryColumn,
    },
  });

  return (
    <>
      <Title small black>
        {locale.nonCashSummary}
      </Title>
      <div className={styles.spacer10} />
      {hasReferenceNumberChange && (
        <>
          <Highlight
            withIcon
            warning
            icon={<ReminderHexIcon />}
            className={styles.reminderHighlight}
            style={{
              opacity: 1,
              transform: "none",
              alignItems: "flex-start",
              paddingTop: 20,
              paddingBottom: 20,
              gap: 12,
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <Text
                color="var(--color-cheddar)"
                style={{ fontSize: 14, fontWeight: 900, lineHeight: "24px" }}
              >
                {locale.reminder}
              </Text>
              <Text color="var(--color-night)" style={{ fontSize: 14 }}>
                {locale.reminderToastMessageGiftCard}
              </Text>
            </div>
          </Highlight>
          <div className={styles.spacer10} />
        </>
      )}
      <div className={styles.tableContainer}>
        <DataTableV2
          {...table}
          hideNoSearchResultRender
          tableVersion="v2"
          hidePagination
          size="small"
        />
      </div>
      <div className={styles.spacer50} />
      <PaymentMethodTransactionModal
        method={selectedPaymentMethod}
        paymentMethodTransactions={paymentMethodTransactions}
        allTransactions={transactionsItems}
        nonCashTransactions={nonCashTransactions}
        transactionsItemDetails={transactionsItemDetails}
        onRefetch={refetchShift}
        pageMode={pageMode}
        updatePaymentMethodRefNumber={handleUpdatePaymentMethodRefNumber}
        updateTransactionPaymentMethod={handleUpdateTransactionPaymentMethod}
        updateTransactionAccountability={updateTransactionAccountability}
        employees={employees}
        {...paymentMethodTransactionModal}
      />
    </>
  );
}
